import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
    fetchTableColumnsRequest,
    selectTableColumns,
    selectTableColumnsLoading,
    selectTableColumnsError,
    selectTableColumnsLastFetched,
} from '../store/slices/database/tableColumnConfigSlice';
import { buildColumnRenderFunction } from '../components/common/DataContainer/columnRenderers';
import { TABLE_COLUMN_TEMPLATES } from '../config/tableColumnTemplates';

// "domainId:tableName" keys already refreshed since this page load. Module-level on purpose:
// it lives in memory only, so it resets on every reload (unlike the persisted configs).
const refreshedThisAppLoad = new Set();

// Stable empty list while the config is loading, so the columns memo doesn't rebuild every render.
const EMPTY_COLUMNS = [];

/**
 * Transforms API response records to column configs
 * Handles the JSONB config column structure from Supabase
 * @param {Array} records - Records from API response
 * @returns {Array} Transformed column configs
 */
const transformApiResponse = (records) => {
    if (!Array.isArray(records)) return [];

    return records.map(record => {
        // Parse config if it's a string (JSONB comes as string from some backends)
        let config = record.config;
        if (typeof config === 'string') {
            try {
                config = JSON.parse(config);
            } catch (e) {
                console.warn(`Failed to parse config for column "${record.column_key}":`, e);
                config = {};
            }
        }
        config = config || {};

        // Parse renderConfig if it's a string within config
        let renderConfig = config.renderConfig;
        if (typeof renderConfig === 'string') {
            try {
                renderConfig = JSON.parse(renderConfig);
            } catch (e) {
                console.warn(`Failed to parse renderConfig for column "${record.column_key}":`, e);
                renderConfig = {};
            }
        }

        return {
            key: record.column_key,
            label: config.label || record.column_key,
            displayOrder: parseInt(record.display_order, 10) || 0,
            isVisible: record.is_visible !== false,
            sortable: config.sortable === true,
            // Sorting this column needs the translated label (database), not the raw value.
            sortByLabel: config.sortByLabel === true,
            align: config.align || 'left',
            minWidth: config.minWidth ? parseInt(config.minWidth, 10) : undefined,
            width: config.width ? parseInt(config.width, 10) : undefined,
            renderType: config.renderType,
            renderConfig: renderConfig,
            cellStyle: config.cellStyle,
            headerStyle: config.headerStyle,
        };
    });
};

/**
 * Custom hook for fetching and transforming table column configurations
 *
 * @param {string} tableName - Identifier for the table (e.g., 'UserCompanies')
 * @param {Object} options - Configuration options
 * @param {Object} options.actions - Action handlers keyed by actionKey (e.g., { onSelect: fn, onEdit: fn })
 * @param {Object} options.context - Context data for conditional rendering (e.g., { selectedCompanyId: 123 })
 * @param {Array} options.fallbackColumns - Override default fallback columns
 * @param {boolean} options.skipFetch - Skip fetching from API (use only fallback/cache)
 *
 * @returns {Object} { columns, loading, error, refetch, isUsingFallback }
 */
export function useTableColumns(tableName, options = {}) {
    const {
        actions = {},
        context = {},
        fallbackColumns,
        skipFetch = false,
    } = options;

    const dispatch = useDispatch();
    const [t] = useTranslation();
    const theme = useTheme();

    // Select from Redux
    const configColumns = useSelector(selectTableColumns(tableName));
    const loading = useSelector(selectTableColumnsLoading(tableName));
    const error = useSelector(selectTableColumnsError(tableName));
    // Set once the config has been fetched successfully (kept in the session cache), even when the
    // table has no rows in tbl_table_column_configs.
    const lastFetched = useSelector(selectTableColumnsLastFetched(tableName));
    const hasConfig = lastFetched != null;

    // Get domain ID from domainConfig
    const domainId = useSelector((state) => state.domainConfig?.data?.domain_id);

    // Cached first, refreshed in the background: the stored config (sessionStorage) renders
    // immediately, and the first visit to each table per app load refetches it, so config edits
    // in tbl_table_column_configs show up after a reload instead of when the session ends. The
    // request reducer keeps the cached columns while it runs, and a failed refresh keeps them too.
    useEffect(() => {
        if (skipFetch || !domainId) return;

        const key = `${domainId}:${tableName}`;
        if (refreshedThisAppLoad.has(key)) return;
        refreshedThisAppLoad.add(key);

        dispatch(fetchTableColumnsRequest({ tableName, domainId }));
    }, [dispatch, skipFetch, tableName, domainId]);

    // Manual refetch function
    const refetch = useCallback(() => {
        if (domainId) {
            dispatch(fetchTableColumnsRequest({ tableName, domainId }));
        }
    }, [dispatch, tableName, domainId]);

    // The template is a fallback, never a placeholder while the config loads: showing it first
    // and then swapping in the real config made buttons flash in and out.
    //   config fetched (or cached) with rows       -> the config, as-is
    //   config fetched, but this domain has 0 rows -> fallbackColumns / template
    //   no config, fetch failed                    -> fallbackColumns / template
    //   no config, skipFetch                       -> fallbackColumns / template (nothing will be fetched)
    //   no config yet, still loading               -> no columns; the page shows its loader (isLoading)
    const hasConfigRows = hasConfig && configColumns.length > 0;
    const useFallback = !hasConfigRows && (hasConfig || Boolean(error) || skipFetch);
    const isLoading = !hasConfigRows && !useFallback;

    const sourceColumns = useMemo(() => {
        if (hasConfigRows) {
            return configColumns;
        }
        if (useFallback) {
            return fallbackColumns || TABLE_COLUMN_TEMPLATES[tableName] || [];
        }
        return EMPTY_COLUMNS;
    }, [hasConfigRows, useFallback, configColumns, fallbackColumns, tableName]);

    // Transform configs into DataTable-compatible columns with render functions
    const columns = useMemo(() => {
        return sourceColumns
            .filter(col => col.isVisible !== false)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map(columnConfig => {
                const {
                    key,
                    label,
                    sortable,
                    sortByLabel,
                    align,
                    minWidth,
                    width,
                    headerStyle,
                    cellStyle,
                    renderConfig,
                } = columnConfig;

                // Support cellStyle from renderConfig (Option B) or from column level
                const finalCellStyle = cellStyle || renderConfig?.cellStyle;

                return {
                    key,
                    label,
                    sortable: sortable ?? false,
                    sortByLabel: sortByLabel ?? false,
                    align: align ?? 'left',
                    minWidth,
                    width,
                    headerStyle,
                    cellStyle: finalCellStyle,
                    render: buildColumnRenderFunction(columnConfig, {
                        actions,
                        context,
                        t,
                        theme,
                    }),
                };
            });
    }, [sourceColumns, actions, context, t, theme]);

    return {
        columns,
        loading,
        // True until the first config arrives (or fails): there are no columns to draw yet.
        isLoading,
        error,
        refetch,
        isFromCache: hasConfigRows,
        isUsingFallback: useFallback,
    };
}

// Export helper for transforming API response (used in saga)
export { transformApiResponse, TABLE_COLUMN_TEMPLATES };
